import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onBack?: () => void;
  variant?: 'global' | 'inline';
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  onBack,
  variant = 'global',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      if (variant === 'global') {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (variant === 'global') {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, onClose, variant]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className={`modal-backdrop ${variant === 'inline' ? 'modal-backdrop--inline' : ''}`}
      onClick={handleBackdropClick}
      style={variant === 'global' ? { zIndex: 2147483646 } : undefined}
    >
      <div
        ref={modalRef}
        className={`modal modal--${size}`}
        onClick={(e) => e.stopPropagation()}
        style={variant === 'global' ? { zIndex: 2147483647 } : undefined}
      >
        <div className="modal-header">
          {onBack && (
            <button
              type="button"
              className="modal-back bg-transparent border-none cursor-pointer flex items-center justify-center rounded-sm"
              onClick={onBack}
              aria-label="Назад"
 style={{ padding: '4px', marginRight: '12px', transition: 'background-color 0.2s' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--gray-100)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="20" height="20" fill="none" stroke="#16C787" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h2 className="modal-title flex-1">{title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );

  // For global modals, use portal to render at document root
  // For inline modals, render in place
  if (variant === 'global') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default Modal;
